import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Col, Container, Row, Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import styled from 'styled-components';
import Demo from './Body/Demo/Demo';
import UserHeaderContainer from './Body/RezRATE/Header/Header.container';
import CandidateHeader from './Header/header.container';
import UserNotification from './UserNotification';
import { DepartmentContext } from 'DepartmentWrapper';
import RezRateBanner from 'Body/RezRATE/Common/RezRateBanner/RezRateBanner';
import { useAuth0 } from './auth0/reactAuth0Spa';
import { fetchDataAgnostic } from './Services/dataApi';
import { formatBearerToken, checkLoginForError } from './Common.functions';
import dayjs from 'dayjs';
import { EvaluatorPortalStateContext } from './Body/RezRATE/EvaluatorPortalStateContext';
import { isRankChartPrintPreviewPath, shouldRenderAdministratorBanners } from './layoutBannerVisibility';
import { getQuestionnaireIssues } from './questionnaireIssues';

const HeaderWrapper = styled.div`
  width: 100%;
  background-color: rgb(255, 255, 255);
  vertical-align: middle;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid transparent;
  border-radius: 4px;
  -webkit-box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
`;

const Layout = (props) => {
  const { children, location, user, embedded, partDocumentTitle } = props;
  const [padding, setPadding] = useState('0');
  const [showUnSetSeasonApplicationDatesBanner, setShowUnSetSeasonApplicationDatesBanner] = useState(false);
  const [questionnaireIssues, setQuestionnaireIssues] = useState([]);
  const [showIssuesModal, setShowIssuesModal] = useState(false);
  const [interviewDates, setInterviewDates] = useState([]);
  const [evaluatorPortalDate, setEvaluatorPortalDate] = useState(null);
  const [evaluatorPortalDateArray, setEvaluatorPortalDateArray] = useState([]);
  const mainContentRef = useRef(null);

  const dContext = useContext(DepartmentContext);
  const { season, department } = dContext;
  const { pathname } = location;
  const isRankChartPrintPreview = isRankChartPrintPreviewPath(pathname);
  const isAdministratorBannerRoute = shouldRenderAdministratorBanners(pathname);
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const evaluatorPortalState = useMemo(
    () => ({
      date: evaluatorPortalDate,
      setDate: setEvaluatorPortalDate,
      dateArray: evaluatorPortalDateArray,
      setDateArray: setEvaluatorPortalDateArray,
    }),
    [evaluatorPortalDate, evaluatorPortalDateArray],
  );

  const setTitle = (conditionalText) => {
    if (!conditionalText) {
      document.title = 'RezRATE';
      return;
    }

    document.title = `RezRATE ${conditionalText}`;
  };

  useEffect(() => {
    switch (true) {
      case pathname.includes('/user/evaluate'):
        setTitle('Evaluator Portal');
        break;
      case pathname === '/':
        setTitle('Program Selection');
        break;
      case pathname.includes('/candidate/schedule/'):
        setTitle(`Schedule ${partDocumentTitle}`);
        break;
      case pathname.includes('/candidate/'):
        setTitle('Registration');
        break;
      default:
        setTitle();
    }
  }, [pathname, partDocumentTitle]);

  useEffect(() => {
    if (window.mobilecheck()) {
      setPadding('0px');
    } else {
      setPadding('5px');
    }
  }, []);

  useEffect(() => {
    const focusSkipTarget = () => {
      if (window.location.hash !== '#acsbContent' || !mainContentRef.current) {
        return;
      }

      mainContentRef.current.focus();
    };

    window.addEventListener('hashchange', focusSkipTarget);
    focusSkipTarget();

    return () => {
      window.removeEventListener('hashchange', focusSkipTarget);
    };
  }, []);

  useEffect(() => {
    if (!isRankChartPrintPreview && season) {
      if (season.ApplicationEndDate == null || season.ApplicationStartDate == null) {
        setShowUnSetSeasonApplicationDatesBanner(true);
      } else {
        setShowUnSetSeasonApplicationDatesBanner(false);
      }
    } else {
      setShowUnSetSeasonApplicationDatesBanner(false);
    }
  }, [season, isRankChartPrintPreview]);

  // Fetch interview dates when department and season are available
  useEffect(() => {
    if (!isRankChartPrintPreview && department && season && department.pk_Department && season.pk_Season) {
      getTokenSilently()
        .then((token) => {
          fetchDataAgnostic(
            'department/InterviewDates',
            { pk_Department: department.pk_Department, pk_Season: season.pk_Season },
            formatBearerToken(token),
          )
            .then((res) => {
              if (res.data && Array.isArray(res.data)) {
                const sortedDates = res.data.sort((a, b) => {
                  const dateA = dayjs(`${a.DateOfInterview} ${a.StartTime}`).valueOf();
                  const dateB = dayjs(`${b.DateOfInterview} ${b.StartTime}`).valueOf();
                  return dateA - dateB;
                });
                setInterviewDates(sortedDates);
              }
            })
            .catch((err) => {});
        })
        .catch((err) => {
          checkLoginForError({ err, loginWithRedirect });
        });
    }
  }, [department, season, getTokenSilently, loginWithRedirect, isRankChartPrintPreview]);

  // Evaluate questionnaire issues
  useEffect(() => {
    setQuestionnaireIssues(isRankChartPrintPreview ? [] : getQuestionnaireIssues(season, interviewDates));
  }, [season, interviewDates, isRankChartPrintPreview]);

  if (!String.prototype.startsWith) {
    String.prototype.startsWith = function(searchString, position) {
      position = position || 0;
      return this.indexOf(searchString, position) === position;
    };
  }

  // different headers for different views
  const isCandidatePage = location.pathname.startsWith('/candidate') || location.pathname.startsWith('/unsubscribe');

  const noHeader =
    location.pathname.startsWith('/files') ||
    location.pathname.startsWith('/viewFile') ||
    location.pathname.startsWith('/logo') ||
    location.pathname.startsWith('/coordinator/schedule') ||
    isRankChartPrintPreview ||
    location.pathname.startsWith('/administrator/evaluate/popout');

  if (noHeader) {
    document.body.style.backgroundColor = 'var(--rr-surface)';
    return children;
  }

  if (isCandidatePage) {
    // document.body.style.backgroundColor = 'rgb(238, 238, 238)';
  } else {
    document.body.style.backgroundColor = 'var(--rr-surface)';
  }

  const conditionalHeader = () => {
    if (isCandidatePage) {
      return (
        <HeaderWrapper>
          <CandidateHeader />
        </HeaderWrapper>
      );
    } else {
      return (
        <div style={{ width: '100%', backgroundColor: '#333333' }}>
          <UserHeaderContainer />
          {showUnSetSeasonApplicationDatesBanner &&
          isAdministratorBannerRoute &&
          department &&
          department.ApplicationSystem === 'direct_application' ? (
            <RezRateBanner
              message={
                <span>
                  Season's Application Start/End dates are not set! <b>Click to here to set them.</b>
                </span>
              }
              textColor={'#ffffff'}
              paddingRight={0}
              paddingLeft={0}
              backgroundColor="#ed723e"
              onClick={() => {
                props.history.push('/administrator/settings/directApplication');
              }}
            />
          ) : null}
          {questionnaireIssues.length > 0 && isAdministratorBannerRoute ? (
            <RezRateBanner
              message={
                <span>
                  {questionnaireIssues.length} issue{questionnaireIssues.length > 1 ? 's' : ''} for department.{' '}
                  <b>Click here to see them.</b>
                </span>
              }
              paddingRight={0}
              paddingLeft={0}
              backgroundColor="#f0ad4e"
              onClick={() => {
                setShowIssuesModal(true);
              }}
            />
          ) : null}
        </div>
      );
    }
  };

  const renderIssuesModal = () => {
    return (
      <Modal
        isOpen={showIssuesModal}
        centered={true}
        style={{ maxWidth: '90vw' }}
        toggle={() => setShowIssuesModal(false)}
      >
        <ModalHeader toggle={() => setShowIssuesModal(false)}>Department Issues</ModalHeader>
        <ModalBody>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
            {questionnaireIssues.map((issue, index) => (
              <li key={index} style={{ marginBottom: '20px' }}>
                <div>
                  <strong>{issue.title}</strong>
                </div>
                <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                  {issue.description.split('. ').map((sentence, idx, arr) => {
                    if (idx === arr.length - 1 && sentence.trim() === '') return null;

                    const fullSentence = sentence + (idx < arr.length - 1 ? '.' : '');
                    const parts = [];
                    let lastIndex = 0;

                    issue.actions.forEach((action) => {
                      const actionIndex = fullSentence.indexOf(action.text, lastIndex);
                      if (actionIndex !== -1) {
                        // Add text before the action
                        if (actionIndex > lastIndex) {
                          parts.push(fullSentence.substring(lastIndex, actionIndex));
                        }
                        // Add the clickable action
                        parts.push(
                          <span
                            key={`${index}-${idx}-${action.text}`}
                            style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                              setShowIssuesModal(false);
                              props.history.push(action.route);
                            }}
                          >
                            {action.text}
                          </span>,
                        );
                        lastIndex = actionIndex + action.text.length;
                      }
                    });

                    // Add remaining text
                    if (lastIndex < fullSentence.length) {
                      parts.push(fullSentence.substring(lastIndex));
                    }

                    return (
                      <span key={`sentence-${idx}`}>
                        {parts.length > 0 ? parts : fullSentence}
                        {idx < arr.length - 1 ? ' ' : ''}
                      </span>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
          <div style={{ textAlign: 'right', marginTop: '20px' }}>
            <Button color="secondary" onClick={() => setShowIssuesModal(false)}>
              Close
            </Button>
          </div>
        </ModalBody>
      </Modal>
    );
  };

  return (
    <EvaluatorPortalStateContext.Provider value={evaluatorPortalState}>
      <div style={{ display: 'flex', height: ' 100%', width: '100%' }}>
        {props.userInfo && props.userInfo.user && props.userInfo.user.demo && <Demo />}

        <Container fluid={true} className="blorp">
          <div id="header_notification_wrapper">
            {!embedded && <Row>{conditionalHeader()}</Row>}
            <UserNotification />
          </div>

          <main id="acsbContent" ref={mainContentRef} tabIndex={-1} style={{ width: '100%', outline: 'none' }}>
            <Row style={{ paddingLeft: padding, paddingRight: padding, paddingTop: padding }}>
              <Col>{children}</Col>
            </Row>
          </main>
        </Container>

        {renderIssuesModal()}
      </div>
    </EvaluatorPortalStateContext.Provider>
  );
};

export default withRouter(Layout);
